/**
 * Página de solicitudes de actualización de perfil.
 * Los directores pueden aprobar/rechazar cambios de información personal de los miembros.
 */

import { useEffect, useState } from "react";
import { useAuthStore } from "@/stores/authStore";
import {
  profileUpdateRequestService,
  type ProfileUpdateRequest,
  parseCurrentData,
} from "@/lib/services/profileUpdateRequests";
import { formatDistanceToNow, format } from "date-fns";
import { es } from "date-fns/locale";

export default function ProfileUpdateRequestsPage() {
  const { profile } = useAuthStore();
  const clubId = profile?.directorOfClub?.id;
  const [requests, setRequests] = useState<ProfileUpdateRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);

  useEffect(() => {
    if (!clubId) {
      setLoading(false);
      return;
    }

    const unsubscribe = profileUpdateRequestService.subscribeToPending(
      clubId,
      (newRequests) => {
        setRequests(newRequests);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [clubId]);

  const handleApprove = async (request: ProfileUpdateRequest) => {
    if (!profile?.id || !clubId) return;

    try {
      setProcessingId(request.id);
      await profileUpdateRequestService.approve(request.id, profile.id);
    } catch (error) {
      console.error("Error al aprobar solicitud:", error);
      alert("Error al aprobar la solicitud");
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (request: ProfileUpdateRequest) => {
    const reason = prompt("Motivo del rechazo (opcional):");
    if (reason === null) return; // Usuario canceló

    if (!profile?.id) return;

    try {
      setProcessingId(request.id);
      await profileUpdateRequestService.reject(
        request.id,
        profile.id,
        reason || undefined,
      );
    } catch (error) {
      console.error("Error al rechazar solicitud:", error);
      alert("Error al rechazar la solicitud");
    } finally {
      setProcessingId(null);
    }
  };

  if (!clubId) {
    return (
      <div className="p-6">
        <p className="text-gray-500 dark:text-gray-400">
          Selecciona un club para ver las solicitudes
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Solicitudes de Cambio de Perfil
        </h1>
        <p className="text-gray-500 dark:text-gray-400">
          Revisa y aprueba las solicitudes de actualización de información
          personal de los miembros
        </p>
      </div>

      {requests.length === 0 ? (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
            Sin solicitudes pendientes
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            No hay solicitudes de cambio de perfil por revisar
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <RequestCard
              key={request.id}
              request={request}
              processingId={processingId}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Componente para mostrar una tarjeta de solicitud con información detallada
function RequestCard({
  request,
  processingId,
  onApprove,
  onReject,
}: {
  request: ProfileUpdateRequest;
  processingId: string | null;
  onApprove: (request: ProfileUpdateRequest) => void;
  onReject: (request: ProfileUpdateRequest) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const currentData = parseCurrentData(request);
  const hasChanges = detectChanges(request, currentData).length > 0;

  const hasSensitiveChanges =
    request.firstName ||
    request.lastName ||
    request.birthDate ||
    request.idDocumentType ||
    request.idDocumentNumber ||
    request.gender;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {request.memberPhotoUrl ? (
                <img
                  src={request.memberPhotoUrl}
                  alt={request.memberName}
                  className="h-12 w-12 rounded-full object-cover"
                />
              ) : (
                <div className="h-12 w-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                  <span className="text-lg font-medium text-primary-600 dark:text-primary-400">
                    {request.memberName.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
            </div>

            {/* Basic Info */}
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white">
                {request.memberName}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {request.memberEmail}
              </p>
              <div className="flex items-center gap-2 mt-1">
                {hasSensitiveChanges && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                    ⚠️ Cambios sensibles
                  </span>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-400">
                Solicitado{" "}
                {formatDistanceToNow(request.createdAt, {
                  addSuffix: true,
                  locale: es,
                })}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onReject(request)}
              disabled={processingId === request.id}
              className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
            >
              Rechazar
            </button>
            <button
              onClick={() => onApprove(request)}
              disabled={processingId === request.id}
              className="px-4 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 flex items-center"
            >
              {processingId === request.id ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Procesando...
                </>
              ) : (
                "Aprobar"
              )}
            </button>
          </div>
        </div>

        {/* Expand button */}
        {hasChanges && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-3 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center gap-1"
          >
            {expanded ? "Ocultar cambios" : "Ver cambios"}
            <svg
              className={`w-4 h-4 transition-transform ${
                expanded ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        )}

        {/* Message */}
        {request.message && (
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 italic bg-gray-50 dark:bg-gray-700/50 rounded p-2">
            "{request.message}"
          </p>
        )}
      </div>

      {/* Expanded changes */}
      {expanded && hasChanges && (
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 p-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Cambios solicitados:
          </h4>
          <div className="space-y-3">
            {detectChanges(request, currentData).map(
              ({ label, oldValue, newValue }) => (
                <ChangeRow
                  key={label}
                  label={label}
                  oldValue={oldValue}
                  newValue={newValue}
                />
              ),
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Componente para mostrar una fila de cambio
function ChangeRow({
  label,
  oldValue,
  newValue,
}: {
  label: string;
  oldValue?: string;
  newValue: string;
}) {
  return (
    <div className="flex items-start gap-4 text-sm">
      <span className="w-32 text-gray-500 dark:text-gray-400 flex-shrink-0">
        {label}:
      </span>
      <div className="flex-1">
        {oldValue && (
          <div className="line-through text-gray-400 dark:text-gray-500">
            {oldValue}
          </div>
        )}
        <div className="text-green-600 dark:text-green-400 font-medium">
          {newValue}
        </div>
      </div>
    </div>
  );
}

// Función para detectar los cambios entre los datos actuales y los solicitados
function detectChanges(
  request: ProfileUpdateRequest,
  currentData: Record<string, unknown>,
): { label: string; oldValue?: string; newValue: string }[] {
  const changes: { label: string; oldValue?: string; newValue: string }[] = [];

  const addChange = (
    label: string,
    oldKey: string,
    newValue: string | undefined,
  ) => {
    if (!newValue) return;
    const old = currentData[oldKey] as string | undefined;
    if (old !== newValue) {
      changes.push({ label, oldValue: old, newValue });
    }
  };

  addChange("Teléfono", "phone", request.phone);
  addChange("Dirección", "address", request.address);
  addChange("EPS/Seguro", "healthInsurance", request.healthInsurance);
  addChange("Tipo de sangre", "bloodType", request.bloodType?.toUpperCase());
  addChange("Nombre", "firstName", request.firstName);
  addChange("Apellido", "lastName", request.lastName);
  addChange(
    "Tipo documento",
    "idDocumentType",
    request.idDocumentType?.toUpperCase(),
  );
  addChange("Número documento", "idDocumentNumber", request.idDocumentNumber);
  addChange(
    "Género",
    "gender",
    request.gender === "male"
      ? "Masculino"
      : request.gender === "female"
        ? "Femenino"
        : undefined,
  );

  if (request.birthDate) {
    changes.push({
      label: "Fecha nacimiento",
      oldValue: undefined,
      newValue: format(request.birthDate, "dd/MM/yyyy"),
    });
  }

  if (request.emergencyContact) {
    const oldContact = currentData.emergencyContact as Record<
      string,
      string
    > | null;
    const newContact = request.emergencyContact;
    const newStr = `${newContact.name} (${newContact.relationship}) - ${newContact.phone}`;
    const oldStr = oldContact
      ? `${oldContact.name} (${oldContact.relationship}) - ${oldContact.phone}`
      : undefined;
    if (newStr !== oldStr) {
      changes.push({
        label: "Contacto emergencia",
        oldValue: oldStr,
        newValue: newStr,
      });
    }
  }

  return changes;
}
